import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Store, MapPin, Award, Mail, Clock, Layers, Wrench } from "lucide-react";

const KIND_COLOR = {
  formulator: "border-bm-cyan/40 text-bm-cyan",
  OEM: "border-bm-amber/40 text-bm-amber",
  "co-packer": "border-bm-green/40 text-bm-green",
  packaging: "border-purple-400/40 text-purple-300",
  ingredient_house: "border-pink-400/40 text-pink-300",
  fragrance_house: "border-yellow-300/40 text-yellow-300",
  fulfilment: "border-blue-300/40 text-blue-300",
};

function SupplierCard({ s, idx }) {
  const cls = KIND_COLOR[s.kind] || "border-white/15 text-bm-muted";
  return (
    <div
      data-testid={`supplier-card-${s.id}`}
      className="bm-card fade-up p-5 flex flex-col gap-3"
      style={{ animationDelay: `${Math.min(idx * 50, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className={`label-mono px-1.5 py-0.5 border ${cls}`} data-testid={`supplier-kind-${s.id}`}>
            {s.kind.toUpperCase().replaceAll("_", " ")}
          </span>
          <h3 className="font-heading text-lg font-black tracking-tight text-white mt-2" data-testid={`supplier-name-${s.id}`}>
            {s.name}
          </h3>
          <div className="flex items-center gap-1 text-xs text-bm-muted mt-0.5">
            <MapPin className="w-3 h-3 text-bm-amber" />{s.city || s.country}
          </div>
        </div>
        <div className="text-right">
          <div className="label-mono text-bm-dim">RATING</div>
          <div className={`font-heading text-3xl font-black leading-none ${s.rating >= 90 ? "text-bm-green" : s.rating >= 80 ? "text-bm-amber" : "text-bm-muted"}`}>
            {s.rating}
          </div>
        </div>
      </div>

      {s.categories?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1">CATEGORIES</div>
          <div className="flex flex-wrap gap-1">
            {s.categories.map((c) => (
              <span key={c} className="text-[11px] font-mono text-bm-cyan border border-bm-cyan/30 px-1.5 py-0.5">{c}</span>
            ))}
          </div>
        </div>
      )}

      {s.capabilities?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1 flex items-center gap-1"><Wrench className="w-3 h-3" />CAPABILITIES</div>
          <div className="flex flex-wrap gap-1">
            {s.capabilities.slice(0, 6).map((c) => (
              <span key={c} className="text-[11px] font-mono text-bm-amber border border-bm-amber/30 px-1.5 py-0.5">{c}</span>
            ))}
          </div>
        </div>
      )}

      {s.certifications?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1 flex items-center gap-1"><Award className="w-3 h-3" />CERTIFICATIONS</div>
          <div className="flex flex-wrap gap-1">
            {s.certifications.map((c) => (
              <span key={c} className="text-[11px] font-mono text-bm-green border border-bm-green/30 px-1.5 py-0.5">{c}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 border-t border-white/5 pt-3 text-center">
        <div>
          <div className="label-mono text-bm-dim flex items-center justify-center gap-1"><Layers className="w-3 h-3" />MOQ</div>
          <div className="font-mono text-sm text-white">{s.moq_units ? `${(s.moq_units/1000).toFixed(0)}k` : "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim">LEAD TIME</div>
          <div className="font-mono text-sm text-white">{s.lead_time_days ? `${s.lead_time_days}d` : "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim">LAST ENGAGED</div>
          <div className="font-mono text-xs text-bm-muted">{s.last_engaged || "—"}</div>
        </div>
      </div>

      {s.notes && (
        <p className="border-t border-white/5 pt-3 text-xs text-bm-muted leading-relaxed italic">
          {s.notes}
        </p>
      )}

      {s.contact_email && (
        <div className="flex items-center gap-2 text-xs font-mono pt-2 border-t border-white/5">
          <Mail className="w-3 h-3 text-bm-amber" />
          <span className="text-bm-cyan">{s.contact_name}</span>
          <span className="text-bm-dim ml-auto">{s.contact_email}</span>
        </div>
      )}
    </div>
  );
}

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.suppliers(kindFilter ? { kind: kindFilter } : {}).then((d) => {
      setItems(d.items || []);
      setLoading(false);
    });
  }, [kindFilter]);

  const kinds = ["formulator", "OEM", "co-packer", "packaging", "ingredient_house", "fragrance_house", "fulfilment"];

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <Store className="w-3.5 h-3.5" />SUPPLIER ROSTER
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            Who builds it.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Formulators, OEMs, ingredient houses, fragrance houses, packaging, and fulfilment partners — Meolaa's vetted
            vendor roster. Sorted by trust rating.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="supplier-kind-filters">
          <button
            onClick={() => setKindFilter("")}
            data-testid="supplier-filter-all"
            className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
              !kindFilter ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
            }`}
          >
            ALL
          </button>
          {kinds.map((k) => (
            <button
              key={k}
              onClick={() => setKindFilter(k)}
              data-testid={`supplier-filter-${k}`}
              className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
                kindFilter === k ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
              }`}
            >
              {k.replaceAll("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING SUPPLIERS...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No suppliers in this category.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="supplier-grid">
        {items.map((s, i) => <SupplierCard key={s.id} s={s} idx={i} />)}
      </div>
    </div>
  );
}
